import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppInput, AppModal, AppPillSwitch, AppSelect } from '../ui';

const formatDossierDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
};

const isCustomTitre = (titre) => {
  const value = String(titre ?? '').trim();
  return value !== '' && value !== 'Ma session' && !value.startsWith('Chiffrage ');
};

export const dossierName = (dossier) => {
  if (isCustomTitre(dossier?.titre)) return dossier.titre.trim();
  return dossier?.label
    || dossier?.entreprise_label
    || dossier?.titre
    || 'Nouveau chiffrage';
};

const PlanDeComDossierInlineName = ({
  dossier,
  disabled = false,
  saving = false,
  className = '',
  onRename,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const displayName = dossierName(dossier);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (disabled || saving) return;
    setDraft(displayName);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const commitEdit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next === displayName) return;
    await onRename?.(dossier.id, next || null);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={`form-control form-control-sm pdc-dossier-inline-name__input ${className}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitEdit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled || saving}
        aria-label="Renommer le chiffrage"
      />
    );
  }

  return (
    <div
      className={`pdc-dossier-inline-name ${className}`}
      onDoubleClick={startEdit}
      title="Double-cliquer pour renommer — Échap pour annuler"
    >
      {displayName}
    </div>
  );
};

const dossierSubtitle = (dossier, commercial) => [
  dossier?.entreprise_label || dossier?.client_label,
  formatDossierDate(dossier?.updated_at),
  commercial,
].filter(Boolean).join(' · ');

const commercialLabel = (dossier, commerciauxById) => {
  if (dossier?.commercial_nom) return dossier.commercial_nom;
  const key = dossier?.commercial_id;
  if (key == null || key === '') return null;
  return commerciauxById.get(String(key)) || null;
};

const PlanDeComDossierSwitcher = ({
  dossiers = [],
  current = null,
  currentId = null,
  disabled = false,
  saving = false,
  canSeeTeamDossiers = false,
  commerciaux = [],
  onRefreshDossiers = null,
  onOpen,
  onNew,
  onDuplicate,
  onDelete,
  onRename,
  hideBar = false,
  open: openProp,
  onOpenChange,
  appearance,
  onToggleStatut,
}) => {
  const [openState, setOpenState] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : openState;
  const setOpen = (value) => {
    if (!isControlled) setOpenState(value);
    onOpenChange?.(value);
  };
  const [scope, setScope] = useState('mine');
  const [search, setSearch] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('');
  const [listing, setListing] = useState(false);
  const searchTimerRef = useRef(null);
  const currentName = dossierName(current);
  const dossierCount = (dossiers ?? []).length;

  const commerciauxById = useMemo(() => {
    const map = new Map();
    (commerciaux ?? []).forEach((c) => {
      if (c?.id != null) map.set(String(c.id), c.nom || String(c.id));
    });
    return map;
  }, [commerciaux]);

  const closeModal = async () => {
    setOpen(false);
    setScope('mine');
    setSearch('');
    setCommercialFilter('');
    if (onRefreshDossiers) {
      await onRefreshDossiers({ scope: 'mine', search: '', commercial_id: '' });
    }
  };

  const refreshList = async (next = {}) => {
    if (!onRefreshDossiers) return;
    const payload = {
      scope: next.scope ?? scope,
      search: next.search ?? search,
      commercial_id: next.commercial_id ?? commercialFilter,
    };
    if (payload.scope !== 'all') {
      payload.commercial_id = '';
    }
    setListing(true);
    try {
      await onRefreshDossiers(payload);
    } finally {
      setListing(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    refreshList();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh only when modal opens
  }, [open]);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    if (nextScope !== 'all') {
      setCommercialFilter('');
      refreshList({ scope: nextScope, commercial_id: '' });
      return;
    }
    refreshList({ scope: nextScope });
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      refreshList({ search: value });
    }, 280);
  };

  const handleCommercialFilterChange = (value) => {
    setCommercialFilter(value);
    refreshList({ commercial_id: value, scope: 'all' });
  };

  const closeAnd = (action) => async (...args) => {
    await closeModal();
    await action?.(...args);
  };

  const sitesLook = appearance === 'sites';

  return (
    <>
      {hideBar ? null : (
      <div className="pdc-dossier-current">
        <div className="pdc-dossier-current__info">
          <span className="pdc-dossier-current__label">Chiffrage en cours</span>
          {current ? (
            <PlanDeComDossierInlineName
              dossier={current}
              disabled={disabled}
              saving={saving}
              className="pdc-dossier-current__name"
              onRename={onRename}
            />
          ) : (
            <strong className="pdc-dossier-current__name">{currentName}</strong>
          )}
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={disabled}
          onClick={() => {
            setScope('mine');
            setSearch('');
            setCommercialFilter('');
            setOpen(true);
          }}
        >
          <Icon icon="mdi:folder-open-outline" className="me-1" />
          Mes chiffrages
          {dossierCount > 0 ? ` (${dossierCount})` : ''}
        </button>
      </div>
      )}

      <AppModal
        show={open}
        className={`pdc-dossier-modal${sitesLook ? ' chiffrage-dossier-modal' : ''}`}
        dialogClassName={sitesLook ? 'chiffrage-dossier-modal__dialog' : ''}
        size={!sitesLook && canSeeTeamDossiers ? 'lg' : undefined}
        centered={!sitesLook}
        scrollable={!sitesLook}
        wrapBody={false}
        onClose={closeModal}
        closeDisabled={disabled}
        title={sitesLook ? undefined : 'Mes chiffrages'}
        labelledBy={sitesLook ? 'chiffrage-dossier-modal-title' : undefined}
        header={sitesLook ? (
          <div className="chiffrage-dossier-modal__head">
            <span className="chiffrage-dossier-modal__title" id="chiffrage-dossier-modal-title">Mes chiffrages</span>
            <AppButton
              variant="link"
              className="chiffrage-dossier-modal__close"
              icon="lucide:x"
              iconWidth={20}
              aria-label="Fermer"
              disabled={disabled}
              onClick={closeModal}
            />
          </div>
        ) : undefined}
      >

              {sitesLook ? (
              <div className="chiffrage-dossier-modal__toolbar">
                <div className="chiffrage-dossier-modal__toolbar-row">
                  {canSeeTeamDossiers ? (
                    <AppPillSwitch
                      variant="segment"
                      className="chiffrage-dossier-modal__tabs"
                      ariaLabel="Périmètre des chiffrages"
                      value={scope}
                      disabled={disabled || listing}
                      onChange={handleScopeChange}
                      options={[
                        { value: 'mine', label: 'Les miens' },
                        { value: 'all', label: 'Tous les commerciaux' },
                      ]}
                    />
                  ) : <span />}
                  <AppButton
                    className="sw-btn sw-btn--dark"
                    icon="lucide:plus"
                    iconWidth={16}
                    disabled={disabled}
                    onClick={() => closeAnd(onNew)()}
                  >
                    Nouveau chiffrage
                  </AppButton>
                </div>
                <div className="chiffrage-dossier-modal__filters">
                  <label className="chiffrage-dossier-modal__search">
                    <Icon icon="lucide:search" width={16} aria-hidden />
                    <AppInput
                      type="search"
                      placeholder="Rechercher un client ou un titre…"
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      disabled={disabled || listing}
                      aria-label="Rechercher un chiffrage"
                    />
                  </label>
                  {canSeeTeamDossiers && scope === 'all' && (commerciaux?.length > 0) ? (
                    <label className="chiffrage-dossier-modal__commercial">
                      <Icon icon="lucide:briefcase" width={15} aria-hidden />
                      <AppSelect
                        value={commercialFilter}
                        onChange={(e) => handleCommercialFilterChange(e.target.value)}
                        disabled={disabled || listing}
                        aria-label="Filtrer par commercial"
                      >
                        <option value="">Tous les commerciaux</option>
                        {commerciaux.map((c) => (
                          <option key={c.id} value={c.id}>{c.nom}</option>
                        ))}
                      </AppSelect>
                      <Icon icon="lucide:chevron-down" width={15} aria-hidden />
                    </label>
                  ) : null}
                </div>
              </div>
              ) : (
              <div className="pdc-dossier-modal__toolbar">
                <button
                  type="button"
                  className="btn btn-primary-600 btn-sm w-100"
                  disabled={disabled}
                  onClick={() => closeAnd(onNew)()}
                >
                  <Icon icon="mdi:plus" className="me-1" />
                  Nouveau chiffrage
                </button>

                {canSeeTeamDossiers ? (
                  <div className="pdc-dossier-modal__scopes" role="tablist" aria-label="Périmètre des chiffrages">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={scope === 'mine'}
                      className={`pdc-dossier-modal__scope${scope === 'mine' ? ' is-active' : ''}`}
                      disabled={disabled || listing}
                      onClick={() => handleScopeChange('mine')}
                    >
                      Les miens
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={scope === 'all'}
                      className={`pdc-dossier-modal__scope${scope === 'all' ? ' is-active' : ''}`}
                      disabled={disabled || listing}
                      onClick={() => handleScopeChange('all')}
                    >
                      Tous les commerciaux
                    </button>
                  </div>
                ) : null}

                <div className="pdc-dossier-modal__filters">
                  <div className="pdc-dossier-modal__search">
                    <Icon icon="mdi:magnify" className="pdc-dossier-modal__search-icon" aria-hidden />
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Rechercher un client ou un titre…"
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      disabled={disabled || listing}
                      aria-label="Rechercher un chiffrage"
                    />
                  </div>
                  {canSeeTeamDossiers && scope === 'all' && (commerciaux?.length > 0) ? (
                    <select
                      className="form-select pdc-dossier-modal__commercial"
                      value={commercialFilter}
                      onChange={(e) => handleCommercialFilterChange(e.target.value)}
                      disabled={disabled || listing}
                      aria-label="Filtrer par commercial"
                    >
                      <option value="">Tous les commerciaux</option>
                      {commerciaux.map((c) => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  ) : null}
                </div>
              </div>
              )}

              {sitesLook ? (
                <div className="chiffrage-dossier-modal__list">
                  {listing && (dossiers ?? []).length === 0 ? (
                    <p className="chiffrage-dossier-modal__empty">Chargement…</p>
                  ) : (dossiers ?? []).length === 0 ? (
                    <p className="chiffrage-dossier-modal__empty">
                      {search || commercialFilter ? 'Aucun chiffrage ne correspond.' : 'Aucun chiffrage pour le moment.'}
                    </p>
                  ) : (
                    <ul className="chiffrage-dossier-modal__items">
                      {(dossiers ?? []).map((dossier) => {
                        const isCurrent = dossier.id === currentId;
                        const name = dossierName(dossier);
                        const commercial = commercialLabel(dossier, commerciauxById) || dossier.created_by_nom || null;
                        const validated = Boolean(dossier.validated);
                        return (
                          <li key={dossier.id} className={`chiffrage-dossier-row${isCurrent ? ' is-current' : ''}`}>
                            <button
                              type="button"
                              className="chiffrage-dossier-row__open"
                              disabled={disabled}
                              onClick={() => (isCurrent ? closeModal() : closeAnd(onOpen)(dossier.id))}
                            >
                              <span className="chiffrage-dossier-row__line">
                                <span className="chiffrage-dossier-row__title">{name}</span>
                                <span className={`chiffrage-dossier-row__status${validated ? ' is-valid' : ''}`}>
                                  {validated ? 'Validé' : 'En cours'}
                                </span>
                                {isCurrent ? <span className="chiffrage-dossier-row__current">Ouvert</span> : null}
                              </span>
                              <span className="chiffrage-dossier-row__sub">{dossierSubtitle(dossier, commercial)}</span>
                            </button>
                            <div className="chiffrage-dossier-row__actions">
                              {onToggleStatut ? (
                                <AppButton
                                  variant="outline-secondary"
                                  className={`chiffrage-dossier-row__icon is-check${validated ? ' is-valid' : ''}`}
                                  icon="lucide:circle-check"
                                  iconWidth={17}
                                  disabled={disabled}
                                  title={validated ? 'Repasser en cours' : 'Marquer comme validé'}
                                  aria-label={validated ? `Repasser ${name} en cours` : `Valider ${name}`}
                                  onClick={() => onToggleStatut(dossier)}
                                />
                              ) : null}
                              <AppButton
                                variant="outline-secondary"
                                className="chiffrage-dossier-row__icon"
                                icon="lucide:copy"
                                iconWidth={16}
                                disabled={disabled}
                                title="Dupliquer"
                                aria-label={`Dupliquer ${name}`}
                                onClick={() => closeAnd(onDuplicate)(dossier.id)}
                              />
                              <AppButton
                                variant="outline-secondary"
                                className="chiffrage-dossier-row__icon is-danger"
                                icon="lucide:trash-2"
                                iconWidth={16}
                                disabled={disabled || isCurrent}
                                title={isCurrent ? 'Chiffrage ouvert : impossible de le supprimer' : 'Supprimer'}
                                aria-label={`Supprimer ${name}`}
                                onClick={() => closeAnd(onDelete)(dossier.id)}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : (
              <div className="modal-body">
                {listing && (dossiers ?? []).length === 0 ? (
                  <p className="text-secondary-light small mb-0">Chargement…</p>
                ) : (dossiers ?? []).length === 0 ? (
                  <p className="text-secondary-light small mb-0">
                    {search || commercialFilter
                      ? 'Aucun chiffrage ne correspond à votre recherche.'
                      : scope === 'all'
                        ? 'Aucun chiffrage trouvé pour l’équipe.'
                        : 'Aucun autre chiffrage pour le moment.'}
                  </p>
                ) : (
                  <ul className="pdc-dossier-list">
                    {(dossiers ?? []).map((dossier) => {
                      const isCurrent = dossier.id === currentId;
                      const name = dossierName(dossier);
                      const commercial = commercialLabel(dossier, commerciauxById);
                      const owner = dossier.created_by_nom || null;
                      return (
                        <li key={dossier.id} className={`pdc-dossier-list__item${isCurrent ? ' is-current' : ''}`}>
                          <div className="pdc-dossier-list__main">
                            <PlanDeComDossierInlineName
                              dossier={dossier}
                              disabled={disabled}
                              saving={saving}
                              className="pdc-dossier-list__name"
                              onRename={onRename}
                            />
                            <div className="pdc-dossier-list__meta">
                              {isCurrent ? 'En cours' : 'Enregistré'}
                              {isCurrent ? ' · Ouvert' : ''}
                              {formatDossierDate(dossier.updated_at) ? ` · ${formatDossierDate(dossier.updated_at)}` : ''}
                              {scope === 'all' && commercial ? ` · ${commercial}` : ''}
                              {scope === 'all' && !commercial && owner ? ` · ${owner}` : ''}
                            </div>
                          </div>
                          <div className="pdc-dossier-list__actions">
                            {isCurrent ? (
                              <span
                                className="pdc-dossier-list__icon-btn is-open"
                                title="Déjà ouvert"
                                aria-label={`${name} : déjà ouvert`}
                              >
                                <Icon icon="mdi:check-circle" width={18} height={18} />
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-primary-600 btn-sm pdc-dossier-list__icon-btn"
                                disabled={disabled}
                                title="Ouvrir"
                                aria-label={`Ouvrir ${name}`}
                                onClick={() => closeAnd(onOpen)(dossier.id)}
                              >
                                <Icon icon="mdi:folder-open-outline" width={18} height={18} />
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm pdc-dossier-list__icon-btn"
                              disabled={disabled}
                              title="Copier"
                              aria-label={`Copier ${name}`}
                              onClick={() => closeAnd(onDuplicate)(dossier.id)}
                            >
                              <Icon icon="mdi:content-copy" width={18} height={18} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm pdc-dossier-list__icon-btn"
                              disabled={disabled}
                              title="Supprimer"
                              aria-label={`Supprimer ${name}`}
                              onClick={() => closeAnd(onDelete)(dossier.id)}
                            >
                              <Icon icon="mdi:trash-can-outline" width={18} height={18} />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              )}
      </AppModal>
    </>
  );
};

export default PlanDeComDossierSwitcher;
